import type { Channel } from '@/Enums/Channel.enum';
import { useGamCreativeSizes } from '@/hooks/forecaster/useGamInventory';
import { useAppSelector } from '@/store/hooks';
import { selectHasGamAccount } from '@/store/selectors/currentWhitelabel.selectors';
import { Checkbox, FormControlLabel, Paper } from '@mui/material';
import { useCallback, useEffect, useRef } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';

interface IProps {
  currentChannel: Channel;
}

const GamCreativeSize = ({ currentChannel }: IProps) => {
  const { control, setValue } = useFormContext();
  const field = `${currentChannel}.creativeSizes`;

  const selected: string[] = useWatch({ control, name: field }) ?? [];

  // Sizes are network-level — fetched once for the account, independent of the
  // selected inventory (matches GAM's line-item UI, which offers the same sizes).
  const hasGamAccount = useAppSelector(selectHasGamAccount);
  const { creativeSizes, isLoading } = useGamCreativeSizes(hasGamAccount);

  // A loaded forecast restores its own sizes — don't auto-default over them.
  const isLoadedForecast = useAppSelector(state => !!state.forecaster.currentForecast?.forecastId);

  // When the list loads (by reference): drop any selected sizes it doesn't offer,
  // default to the first when none remain (skipped for a loaded forecast).
  const defaultedSizesRef = useRef<typeof creativeSizes | null>(null);
  useEffect(() => {
    const prev = defaultedSizesRef.current;
    if (isLoading || creativeSizes === prev) return;
    defaultedSizesRef.current = creativeSizes;
    if (!creativeSizes.length) return;
    const available = creativeSizes.map(s => `${s.width}x${s.height}`);
    const kept = selected.filter(v => available.includes(v));
    if (!kept.length && !isLoadedForecast) {
      setValue(field, [available[0]]);
    } else if (kept.length !== selected.length) {
      setValue(field, kept);
    }
  }, [creativeSizes, isLoading, selected, setValue, field, isLoadedForecast]);

  const toggle = useCallback(
    (value: string) => {
      const next = selected.includes(value) ? selected.filter(v => v !== value) : [...selected, value];
      setValue(field, next);
    },
    [selected, setValue, field],
  );

  return (
    <Paper className='col-span-3 p-4 print:p-[10px] break-after-page'>
      <div className='flex flex-col gap-2'>
        <p className='text-[#2E2C34] text-[18px] font-semibold'>Creative Size</p>
        {isLoading && <p className='text-sm text-gray-400'>Loading…</p>}
        {!isLoading && !creativeSizes.length && (
          <p className='text-sm text-gray-400'>No video creative sizes available</p>
        )}
        <div className='flex flex-col'>
          {creativeSizes.map(size => {
            const value = `${size.width}x${size.height}`;
            return (
              <FormControlLabel
                key={size.label}
                sx={{ marginLeft: 0, fontSize: '14px' }}
                control={
                  <Checkbox sx={{ padding: '6px' }} checked={selected.includes(value)} onChange={() => toggle(value)} />
                }
                label={size.label}
              />
            );
          })}
        </div>
      </div>
    </Paper>
  );
};

export default GamCreativeSize;
